import { defineStore } from 'pinia';
import { useChat } from '@ai-sdk/vue';
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithToolCalls } from 'ai';
import { AGENT_TOOLS, OPEN_TOOLSET_TOOL_NAME, type AgentPrepared, type AgentToolDef } from '#shared/agent/tool-defs';
import { createConfirmGate, type ConfirmTicket } from '#shared/agent/confirm-gate';
import { executeAgentTool, prepareAgentTool } from '~/utils/agent-tool-executor';
import { exportUserSession } from '~/utils/helpers';

interface AgentToolCall {
  toolCallId: string;
  toolName: string;
  input: unknown;
}

/** Conversation persistence: survives refresh, capped, and expired with the session horizon. */
export const SAMMI_CHAT_STORAGE_KEY = 'sammi-chat-v1';
const STORAGE_TTL_MS = 12 * 60 * 60 * 1000;
const STORAGE_MAX_MESSAGES = 40;

const isToolPart = (p: any) =>
  typeof p?.type === 'string' && (p.type.startsWith('tool-') || p.type === 'dynamic-tool');

const loadPersistedMessages = (): any[] => {
  if (!import.meta.client) return [];
  try {
    const raw = localStorage.getItem(SAMMI_CHAT_STORAGE_KEY);
    if (!raw) return [];
    const saved = JSON.parse(raw);
    if (!saved?.savedAt || Date.now() - saved.savedAt > STORAGE_TTL_MS) {
      localStorage.removeItem(SAMMI_CHAT_STORAGE_KEY);
      return [];
    }
    // Drop tool parts without a final output (e.g. a confirmation left pending
    // at refresh) — dangling tool calls make the server reject the history.
    return (saved.messages ?? [])
      .map((m: any) => ({
        ...m,
        parts: (m.parts ?? []).filter(
          (p: any) => !isToolPart(p) || p.state === 'output-available' || p.state === 'output-error'
        ),
      }))
      .filter((m: any) => (m.parts ?? []).length > 0);
  } catch {
    return [];
  }
};

const clearPersistedMessages = () => {
  if (import.meta.client) localStorage.removeItem(SAMMI_CHAT_STORAGE_KEY);
};

export interface PendingConfirmation {
  toolCall: AgentToolCall;
  def: AgentToolDef;
  /** Read-only data the card shows (for example the rendered email preview) */
  prepared: AgentPrepared;
  /** The gate's ticket; only the card's click handler can turn it into an Approval */
  ticket: ConfirmTicket;
}

const OPEN_STATE_KEY = 'sammi-open-v1';

export const useAgentStore = defineStore('agent', () => {
  // Window open/closed survives refresh alongside the conversation itself
  const isOpen = ref(import.meta.client && localStorage.getItem(OPEN_STATE_KEY) === 'true');
  watch(isOpen, (open) => {
    if (import.meta.client) localStorage.setItem(OPEN_STATE_KEY, String(open));
  });
  // Queue: the model can emit several confirmation-required tool calls in one
  // assistant turn; each needs its own output or the loop never resumes
  const pendingConfirmations = ref<PendingConfirmation[]>([]);
  const pendingConfirmation = computed(() => pendingConfirmations.value[0] ?? null);
  // Bumped on reset so in-flight tool executions don't write into the new conversation
  let generation = 0;
  // Confirm-before-write gate: a write call only opens a ticket; it runs only with the
  // single-use Approval issued when the user clicks the card's button.
  const confirmGate = createConfirmGate();

  // Static init object on purpose — a getter/reactive init would recreate the
  // chat and wipe the conversation whenever a dependency changes.
  const chat = useChat({
    messages: loadPersistedMessages(),
    transport: new DefaultChatTransport({
      api: '/api/agent/chat',
      // Resolved per request, so a refreshed token is always picked up
      headers: () => ({ Authorization: `Bearer ${exportUserSession().value}` }),
    }),
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    async onToolCall({ toolCall }) {
      if (toolCall.dynamic) return;
      // open_toolset executes on the server (mixed execution); its input still
      // streams here as a normal tool call, so skip it — the server output
      // follows on the same stream. Adding an output here would corrupt it.
      if (toolCall.toolName === OPEN_TOOLSET_TOOL_NAME) return;
      const def = AGENT_TOOLS.find((t) => t.name === toolCall.toolName);
      if (!def) {
        chat.addToolOutput({
          state: 'output-error',
          tool: toolCall.toolName,
          toolCallId: toolCall.toolCallId,
          errorText: `Unknown tool: ${toolCall.toolName}`,
        });
        return;
      }
      if (def.requiresConfirmation) {
        const startedGeneration = generation;
        // Read-only preparation (for example rendering the email preview); an error skips the card
        const prepared = await prepareAgentTool(def, toolCall.input);
        if (startedGeneration !== generation) return; // conversation was reset mid-flight
        if (prepared.error) {
          chat.addToolOutput({
            tool: toolCall.toolName,
            toolCallId: toolCall.toolCallId,
            output: { error: prepared.error },
          });
          return;
        }
        const parsedInput = def.inputSchema.safeParse(toolCall.input);
        const ticket = confirmGate.request({
          toolCallId: toolCall.toolCallId,
          toolName: toolCall.toolName,
          def,
          input: parsedInput.success ? parsedInput.data : toolCall.input,
          prepared,
        });
        // UI renders ConfirmActionCard; confirmPending/declinePending resolve it
        pendingConfirmations.value = [
          ...pendingConfirmations.value,
          {
            toolCall: { toolCallId: toolCall.toolCallId, toolName: toolCall.toolName, input: ticket.input },
            def,
            prepared,
            ticket,
          },
        ];
        return;
      }
      const startedGeneration = generation;
      const output = await executeAgentTool(def, toolCall.input);
      if (startedGeneration !== generation) return; // conversation was reset mid-flight
      chat.addToolOutput({ tool: toolCall.toolName, toolCallId: toolCall.toolCallId, output });
    },
  });

  // Force the completed turn to repaint. messages is a ShallowRef and the
  // closing streamed text often mutates the last part.text IN PLACE without
  // reassigning .value (seen on Azure's Responses stream), so the final blurb
  // doesn't paint until the next sendMessage reassigns the array. triggerRef
  // proved unreliable through the Pinia indirection; a fresh array reference is
  // an unambiguous signal. Do it now and again next tick in case the text lands
  // a tick after status settles.
  const flushMessages = () => {
    chat.messages.value = [...(chat.messages.value ?? [])];
  };

  // Persist when a turn settles (covers streamed text, tool outputs, and errors);
  // per-token writes during streaming would be wasteful.
  watch(chat.status, (status) => {
    if (status !== 'ready' && status !== 'error') return;
    flushMessages();
    nextTick(flushMessages);
    const messages = chat.messages.value ?? [];
    if (!messages.length) {
      clearPersistedMessages();
      return;
    }
    try {
      localStorage.setItem(
        SAMMI_CHAT_STORAGE_KEY,
        JSON.stringify({ savedAt: Date.now(), messages: messages.slice(-STORAGE_MAX_MESSAGES) })
      );
    } catch {
      // Quota exceeded — drop persistence rather than break the chat
      clearPersistedMessages();
    }
  });

  /** Called only from the confirm card's button. `extraBody` holds fields the USER typed on the card. */
  const confirmPending = async (extraBody?: Record<string, unknown>) => {
    const pending = pendingConfirmation.value;
    if (!pending) return;
    pendingConfirmations.value = pendingConfirmations.value.slice(1);
    const startedGeneration = generation;
    const approval = confirmGate.approve(pending.ticket);
    const output = await executeAgentTool(pending.def, pending.ticket.input, { approval: approval ?? undefined, extraBody });
    if (startedGeneration !== generation) return; // conversation was reset mid-flight
    chat.addToolOutput({ tool: pending.toolCall.toolName, toolCallId: pending.toolCall.toolCallId, output });
  };

  const declinePending = () => {
    const pending = pendingConfirmation.value;
    if (!pending) return;
    pendingConfirmations.value = pendingConfirmations.value.slice(1);
    confirmGate.cancel(pending.toolCall.toolCallId);
    chat.addToolOutput({
      tool: pending.toolCall.toolName,
      toolCallId: pending.toolCall.toolCallId,
      output: { declined: 'User declined the action' },
    });
  };

  const sendMessage = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    // A pending confirmation means the last assistant message has an unresolved
    // tool call; sending now would make the server reject the message history
    if (pendingConfirmations.value.length) return;
    if (chat.error.value) chat.clearError();
    return chat.sendMessage({ text: trimmed });
  };

  const reset = () => {
    generation++;
    chat.stop();
    chat.clearError();
    pendingConfirmations.value = [];
    confirmGate.clear();
    chat.messages.value = [];
    clearPersistedMessages();
  };

  return {
    isOpen,
    pendingConfirmation,
    pendingConfirmations,
    // ShallowRefs straight from useChat — Pinia unwraps them like any ref
    messages: chat.messages,
    status: chat.status,
    error: chat.error,
    sendMessage,
    stop: chat.stop,
    confirmPending,
    declinePending,
    reset,
  };
});
